import React, {useEffect, useRef, useState} from 'react';
import {useRouter} from 'next/router';
import _ from 'lodash';

import SearchModal from './SearchModal';
import {SearchIcon} from '../svg';
import {cx} from '../../util';

const Search = ({
  className,
  onClick = _.noop,
  label = 'Site search',
  // Keep the full input at every width. The legacy header collapses to an
  // icon between md and xl because its menu links crowd the bar; the top bar
  // has the room, and the input popping in and out reads as a glitch there.
  expanded = false,
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const router = useRouter();

  // Keyboard shortcut listener
  useEffect(() => {
    const searchShortcutListener = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        // Both the desktop and mobile navs mount a Search; only the visible
        // instance responds so a single modal opens.
        if (!containerRef.current || containerRef.current.offsetParent === null) return;
        e.preventDefault();
        setOpen(true);
      }
    };

    window.addEventListener('keydown', searchShortcutListener);
    return () => window.removeEventListener('keydown', searchShortcutListener);
  }, []);

  // Close on route change
  useEffect(() => {
    setOpen(false);
  }, [router.asPath]);

  return (
    <div className="relative" role="search" aria-label={label} ref={containerRef}>
      <div className={className}>
        <button
          type="button"
          aria-label={label}
          onClick={() => setOpen(true)}
          className={cx(
            'flex w-full items-center gap-2 rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-400 transition hover:border-gray-400',
            !expanded && 'md:hidden xl:flex',
          )}
        >
          <SearchIcon className="h-4 w-4 shrink-0 pointer-events-none" />
          <span className="truncate">Search or ask AI...</span>
          <span
            className={cx(
              'ml-auto hidden rounded border border-gray-200 px-1.5 py-0.5 text-xs text-gray-400',
              expanded ? 'xl:block' : '2xl:block',
            )}
          >
            ⌘K
          </span>
        </button>
      </div>

      {/* Fallback search icon to display on medium width screens */}
      {!expanded && (
        <button
          type="button"
          aria-label={label}
          onClick={() => setOpen(true)}
          className={cx('ml-2 mr-0.5 mb-0.5 hidden md:block xl:hidden')}
        >
          <SearchIcon stroke="#444" strokeWidth="3" className="h-5 w-5" />
        </button>
      )}

      <SearchModal
        isOpen={open}
        onClose={() => setOpen(false)}
        onClickResult={onClick}
      />
    </div>
  );
};

export default Search;
